import { History, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import type { PatientResponseDto } from "~/lib/api";
import { fetchCurrentUserId, fetchSessionPatients } from "~/lib/patients";
import { canAddPatient } from "~/lib/permissions";
import NotFoundRoute from "~/routes/not-found";
import { useSession } from "~/session/session-context";

import { CarriedOverTasksBanner } from "./carried-over-tasks-banner";
import { EscalatedTasksBanner } from "./escalated-tasks-banner";
import { OverdueTasksBanner } from "./overdue-tasks-banner";
import { PatientCard } from "./patient-card";
import { PRIORITY_META, PRIORITY_ORDER } from "./priority";

type State =
  | { kind: "loading" }
  | { kind: "not-found" }
  | { kind: "error"; message: string }
  // `canAdd` is the shared rule (HAN-49): true only for the outgoing doctor on a
  // draft/active session — i.e. exactly when the action bar should show.
  | { kind: "ready"; patients: PatientResponseDto[]; canAdd: boolean };

/**
 * The session's patient list (HAN-47): the main view of a session, rendered at
 * the `/session/:id` index inside `<SessionLayout>` (so the session — and the
 * caller's ward access — is already resolved). Patients are grouped into High /
 * Medium / Low sections, searchable by name or diagnosis (client-side, since the
 * list is small), and each card links to the patient detail screen. The outgoing
 * doctor additionally gets an action bar to add patients and start the handover.
 */
export function PatientListScreen(): JSX.Element {
  const { session } = useSession();
  const sessionId = session.id;

  const [tab, setTab] = useState<"active" | "completed">("active");
  const [state, setState] = useState<State>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const [query, setQuery] = useState("");

  const load = useCallback(
    async (signal: { cancelled: boolean }): Promise<void> => {
      setState({ kind: "loading" });

      const archived = tab === "completed";
      const [currentUserId, patientsResult] = await Promise.all([
        fetchCurrentUserId(),
        fetchSessionPatients(sessionId, archived),
      ]);
      if (signal.cancelled) return;

      if (patientsResult.status === "not-found") {
        setState({ kind: "not-found" });
        return;
      }
      if (patientsResult.status === "error") {
        setState({ kind: "error", message: patientsResult.message });
        return;
      }

      // The action bar is the outgoing doctor's, and only while the session can
      // still be edited. The server enforces the same on every write.
      const canAdd = canAddPatient(currentUserId ? { id: currentUserId } : null, session);

      setState({ kind: "ready", patients: patientsResult.data, canAdd });
    },
    [sessionId, session, tab],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load, reloadKey]);

  if (state.kind === "loading") {
    return (
      <div className="space-y-3" role="status" aria-live="polite" aria-label="Loading patients">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-xl border bg-muted/40" />
        ))}
      </div>
    );
  }

  if (state.kind === "not-found") {
    return <NotFoundRoute />;
  }

  if (state.kind === "error") {
    return (
      <Alert variant="destructive">
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>{state.message}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setReloadKey((k) => k + 1);
            }}
          >
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const { patients, canAdd } = state;
  const q = query.trim().toLowerCase();
  const visible =
    q === ""
      ? patients
      : patients.filter(
          (p) => p.fullName.toLowerCase().includes(q) || p.diagnosis.toLowerCase().includes(q),
        );

  return (
    <div className="space-y-4">
      <OverdueTasksBanner sessionId={sessionId} patients={patients} />
      <EscalatedTasksBanner sessionId={sessionId} patients={patients} />
      <CarriedOverTasksBanner sessionId={sessionId} patients={patients} />

      {/* Active / Completed toggle — two buttons, matching the Button-pair
          pattern used elsewhere in the session screens (no Tabs component). */}
      <div className="flex items-center gap-1 rounded-lg border p-1 w-fit">
        <Button
          variant={tab === "active" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => {
            setTab("active");
          }}
        >
          Active
        </Button>
        <Button
          variant={tab === "completed" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => {
            setTab("completed");
          }}
        >
          Completed
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* Any ward member can read the audit log (E8.2) — placed before the
            outgoing-doctor actions so it's discoverable for the incoming doctor
            too. The link itself is plain; the icon hints at its read-only role. */}
        <Button asChild variant="ghost" size="sm">
          <Link to={`/session/${sessionId}/audit`}>
            <History className="h-4 w-4" aria-hidden="true" />
            View audit log
          </Link>
        </Button>
        {canAdd ? (
          <>
            <Button asChild variant="outline" size="sm">
              <Link to={`/session/${sessionId}/patient/new`}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add patient
              </Link>
            </Button>
            {patients.length === 0 ? (
              <Button size="sm" disabled>
                Start handover
              </Button>
            ) : (
              <Button asChild size="sm">
                <Link to={`/session/${sessionId}/start-handover`}>Start handover</Link>
              </Button>
            )}
          </>
        ) : null}
      </div>

      {patients.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
          {tab === "completed"
            ? "No completed patients."
            : canAdd
              ? "No patients yet. Click + Add patient to start."
              : "No active patients."}
        </div>
      ) : (
        <>
          <div className="sticky top-0 z-10 bg-background py-1">
            <Input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              placeholder="Search by name or diagnosis"
              aria-label="Search patients"
            />
          </div>

          {visible.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No patients match your search.
            </p>
          ) : (
            <div className="space-y-6">
              {PRIORITY_ORDER.map((priority) => {
                const group = visible.filter((p) => p.priority === priority);
                if (group.length === 0) return null;
                const meta = PRIORITY_META[priority];
                return (
                  <section key={priority} className="space-y-2">
                    <h2
                      className={`text-sm font-semibold uppercase tracking-wide ${meta.headerClass}`}
                    >
                      {meta.label}
                    </h2>
                    <div className="space-y-2">
                      {group.map((patient) => (
                        <PatientCard key={patient.id} sessionId={sessionId} patient={patient} />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
